import {Alert, closeCurrentDialog, Form, FormField, showNotification} from 'platform/components';
import {VStack} from 'platform/foundation';
import {z} from 'zod';

import {usePatchCoreExchangeCancelDeliveryMutation} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {DialogActions} from './DialogActions';

const CancelDeliveryFormSchema = z.object({
  storageNote: z.string(),
});

type CancelDeliveryForm = z.infer<typeof CancelDeliveryFormSchema>;

interface CancelDeliveryDialogProps extends RequiredTestIdProps {
  coreExchangeId: string;
  onSaved: VoidFunction;
}

export function CancelDeliveryDialog(props: CancelDeliveryDialogProps) {
  const [patchCancelDelivery, {isLoading}] = usePatchCoreExchangeCancelDeliveryMutation();

  const handleSubmit = async (values: CancelDeliveryForm) => {
    await patchCancelDelivery({coreExchangeId: props.coreExchangeId, body: values})
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('general.notifications.successfullyUpdated'));
        props.onSaved();
        closeCurrentDialog();
      })
      .catch(handleApiError);
  };

  return (
    <Form<CancelDeliveryForm>
      experimentalZodSchema={CancelDeliveryFormSchema}
      onSubmit={handleSubmit}
      defaultValues={{storageNote: ''}}
    >
      {(control) => (
        <VStack spacing={4}>
          <Alert
            type="inline"
            variant="warning"
            message={i18n.t('entity.warehouse.labels.coreExchangeCancelDeliveryWarning')}
            data-testid={suffixTestId('warning', props)}
          />
          <FormField
            control={control}
            type="textarea"
            name="storageNote"
            label={i18n.t('entity.warehouse.labels.note')}
            data-testid={suffixTestId('inputs.storageNote', props)}
          />
          <DialogActions
            control={control}
            isLoading={isLoading}
            confirmTitle={i18n.t('entity.warehouse.actions.coreExchangeFinish')}
            data-testid={props['data-testid']}
          />
        </VStack>
      )}
    </Form>
  );
}
